/** Progressive enhancement only: content is readable before JS, without motion and in the editor. */
class CompanyObjectionsElement extends HTMLElement {
  static observedAttributes = ['data-motion'];
  private frame = 0;
  private mutation?: MutationObserver;
  private resize?: ResizeObserver;
  private intersection?: IntersectionObserver;
  private visible = false;
  private reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    window.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    window.addEventListener('resize', this.schedule, { passive: true });
    this.reducedMotion.addEventListener('change', this.schedule);
    this.mutation = new MutationObserver(this.schedule);
    this.mutation.observe(this, { childList: true, subtree: true });
    this.resize = new ResizeObserver(this.schedule);
    this.resize.observe(this);
    // The story follows the hero but used to force layout during every hero
    // frame. Resume before it enters the viewport, including after scroll jumps.
    this.intersection = new IntersectionObserver(entries => {
      this.visible = entries.some(entry => entry.isIntersecting);
      if (this.visible) this.schedule();
    }, { rootMargin: '160px 0px' });
    this.intersection.observe(this);
    this.schedule();
  }

  attributeChangedCallback() { if (this.isConnected) this.schedule(); }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.schedule);
    this.reducedMotion.removeEventListener('change', this.schedule);
    this.mutation?.disconnect();
    this.resize?.disconnect();
    this.intersection?.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  private onScroll = () => { if (this.visible) this.schedule(); };

  private schedule = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      const animated = this.dataset.motion !== 'off' && !this.reducedMotion.matches;
      this.classList.toggle('has-story-motion', animated);
      const heading = this.querySelector<HTMLElement>('.vp-company-objections-heading');
      this.classList.toggle('has-tall-heading', Boolean(heading && heading.offsetHeight > window.innerHeight - 160));
      const cards = [...this.querySelectorAll<HTMLElement>('[data-story-card]')];
      let active = 0;
      cards.forEach((card, index) => {
        if (card.getBoundingClientRect().top <= window.innerHeight * 0.55) active = index;
      });
      cards.forEach((card, index) => { card.classList.toggle('is-current', animated && index === active); });
      const counter = this.querySelector('[data-story-current]');
      const value = String(active + 1).padStart(2, '0');
      if (counter && counter.textContent !== value) counter.textContent = value;
      this.style.setProperty('--vp-story-progress', String(cards.length ? (active + 1) / cards.length : 0));
    });
  };
}

if (!customElements.get('vp-company-objections')) customElements.define('vp-company-objections', CompanyObjectionsElement);
